import { applyLobbyPatch, parseLobbySettingsPatch, type LobbySettingsPatch } from '../shared/lobby.js';
import { raceEventMode } from '../shared/race-events.js';
import type { MapDefinition, RaceSettings } from '../shared/types.js';

export type LobbyKind = 'online' | 'offline';

const STORAGE_KEY = 'nightshift:lobby-setup:v1';
const INDEXED_DATABASES = ['nightshift-local-races', 'nightshift-origin-locks'] as const;

type StoredLobbySetups = Partial<Record<LobbyKind, LobbySettingsPatch>>;

/**
 * Complete last lobby a driver hosted (online) or raced alone (offline):
 * track, format, laps, atmosphere, rule toggles, the per-slot rival roster,
 * visibility and slot count. New lobbies of the same kind start from it.
 */
export function loadLobbySetup(kind: LobbyKind): LobbySettingsPatch | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as StoredLobbySetups | null;
    const candidate = parsed && typeof parsed === 'object' ? parsed[kind] : undefined;
    return candidate ? parseLobbySettingsPatch(candidate) : null;
  } catch {
    // A damaged or foreign value simply falls back to the profile defaults.
    return null;
  }
}

export function saveLobbySetup(kind: LobbyKind, settings: RaceSettings): void {
  const setup = lobbySetupFromSettings(settings);
  try {
    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as StoredLobbySetups;
    const next: StoredLobbySetups = { ...(current && typeof current === 'object' ? current : {}), [kind]: setup };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage is optional; the lobby itself keeps working.
  }
}

/** Settings → the replayable patch that recreates them on any later visit. */
export function lobbySetupFromSettings(settings: RaceSettings): LobbySettingsPatch {
  const roster = settings.npcRoster ?? Array.from({ length: settings.npcCount }, () => settings.npcDifficulty);
  return {
    mapId: settings.mapId,
    eventMode: raceEventMode(settings),
    laps: settings.laps ?? null,
    ...(settings.timeOfDay ? { timeOfDay: settings.timeOfDay } : {}),
    ...(settings.weather ? { weather: settings.weather } : {}),
    traffic: settings.traffic === true,
    collisions: settings.collisions !== false,
    antiCheat: settings.antiCheat === true,
    npcRoster: roster,
    visibility: settings.visibility === 'public' ? 'public' : 'private',
    ...(settings.maxPlayers >= 2 ? { maxPlayers: settings.maxPlayers } : {}),
    ...(settings.arcadePreset ? { arcadePreset: settings.arcadePreset } : {}),
  };
}

/** Applies a saved setup over defaults; a track no longer published is skipped. */
export function applyLobbySetup(
  base: RaceSettings,
  setup: LobbySettingsPatch | null,
  maps: readonly MapDefinition[],
  online: boolean,
): RaceSettings {
  if (!setup) return base;
  const map = maps.find((candidate) => candidate.id === setup.mapId) ?? maps.find((candidate) => candidate.id === base.mapId);
  if (!map) return base;
  const patch = map.id === setup.mapId ? setup : { ...setup, mapId: undefined, timeOfDay: undefined, weather: undefined };
  return applyLobbyPatch(base, patch, map, { online });
}

/**
 * Wipes everything NIGHTSHIFT keeps in this browser: profile, settings,
 * bindings, career, ghosts, saved races and offline checkpoints.
 */
export async function clearAllLocalData(): Promise<void> {
  try {
    localStorage.clear();
  } catch {
    // Nothing persisted to clear.
  }
  try {
    sessionStorage.clear();
  } catch {
    // Same as above.
  }
  if (typeof indexedDB === 'undefined') return;
  await Promise.all(INDEXED_DATABASES.map((name) => new Promise<void>((resolve) => {
    // A deletion blocked by another open tab must not hang the reset.
    const timer = setTimeout(resolve, 1_500);
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = request.onerror = request.onblocked = () => {
        clearTimeout(timer);
        resolve();
      };
    } catch {
      clearTimeout(timer);
      resolve();
    }
  })));
}
